import React from 'react';
import { Box, Card, Image, Typography, Icon, Tooltip, Link } from '@drone-js/core';
import { TopSubcategory } from 'core/type/BootstrapConfig.types';
import { StringFormatter } from '@agoda/react-handyman';
import { getImageUrl, getSearchPageWithSubcategoryIdUrl } from 'core/utils/activityUrlGenerator';
import { useSelector } from 'react-redux';
import { selectConfigFromBootstrap } from 'store/slice/bootstrap';
import { CategoryKeyRecord, SubcategoryCms, SubcategoryId, SubcategoryParentId } from 'core/type/Subcategory.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesQuickSubcategoryFilterItemClickEventActivitiesFieldsV1,
    ActivitiesQuickSubcategoryFilterItemHoverEventActivitiesFieldsV1,
    ActivitiesQuickSubcategoryFilterItemSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesQuickSubcategoryFilterItemClickEventActivitiesManifestV1,
    ActivitiesQuickSubcategoryFilterItemHoverEventActivitiesManifestV1,
    ActivitiesQuickSubcategoryFilterItemSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { useInView } from 'react-intersection-observer';

interface Props {
    index: number;
    item: TopSubcategory;
    activitiesCountText: string;
}
const TopSubcategoryCard: React.FC<Props> = (props) => {
    const { subcategoryId, activityCount } = props.item;
    const config = useSelector(selectConfigFromBootstrap);
    const { cms, behavior } = config;
    const subcategoryNameTranslated = cms[SubcategoryCms[subcategoryId as SubcategoryId]];
    const subcategoryQueryKey = CategoryKeyRecord[subcategoryId as SubcategoryId]?.queryKey || '';
    const openInNewTab = behavior.search.openDetailPageInNewTab;

    const onSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesQuickSubcategoryFilterItemSeenEventActivitiesFieldsV1>(ActivitiesQuickSubcategoryFilterItemSeenEventActivitiesManifestV1.ids, {
                action_element_index: props.index + 1,
                action_element_value: subcategoryId,
                activities_categories: SubcategoryParentId[subcategoryId as SubcategoryId],
                activities_count: activityCount,
            });
        }
    };
    const { ref } = useInView({
        threshold: 0.75,
        triggerOnce: true,
        onChange: onSeen,
    });

    const onClick = () => {
        trackElementEvent<ActivitiesQuickSubcategoryFilterItemClickEventActivitiesFieldsV1>(ActivitiesQuickSubcategoryFilterItemClickEventActivitiesManifestV1.ids, {
            action_element_index: props.index + 1,
            action_element_value: subcategoryId,
            activities_categories: SubcategoryParentId[subcategoryId as SubcategoryId],
            activities_count: activityCount,
        });
    };

    const onHover = () => {
        trackElementEvent<ActivitiesQuickSubcategoryFilterItemHoverEventActivitiesFieldsV1>(ActivitiesQuickSubcategoryFilterItemHoverEventActivitiesManifestV1.ids, {
            action_element_index: props.index + 1,
            action_element_value: subcategoryId,
            activities_categories: SubcategoryParentId[subcategoryId as SubcategoryId],
            activities_count: activityCount,
        });
    };

    if (!subcategoryNameTranslated || !subcategoryQueryKey) {
        return null;
    }

    return (
        <Link href={getSearchPageWithSubcategoryIdUrl(subcategoryQueryKey)} target={openInNewTab ? '_blank' : '_self'} display="flex" onClick={onClick} onMouseEnter={onHover}>
            <Card type="interactive" display="flex" width="100%" height="72px" p="12" alignItems="center" ref={ref} data-element-name="activities-quick-subcategory-filter-item">
                <Tooltip title={subcategoryNameTranslated} pointerPosition="below" usePortal={true}>
                    <Box display="flex" flexDirection="row" width="232px" columnGap="12" alignItems="center" tabIndex={-1}>
                        <Image
                            width="48px"
                            height="48px"
                            borderRadius="8"
                            objectFit="cover"
                            src={getImageUrl(`subcategory/subcategory-${subcategoryId}.jpg`)}
                            alt={subcategoryNameTranslated}
                        />
                        <Box display="flex" flexDirection="column" flexGrow={1} rowGap="4" justifyContent="center" maxWidth="144px">
                            <Typography variant="body-default-strong" lineClamp={2}>
                                {subcategoryNameTranslated}
                            </Typography>
                            <Typography variant="body-s" textColor="base.subtle" lineClamp={1}>
                                {StringFormatter.format(props.activitiesCountText, activityCount.toLocaleString())}
                            </Typography>
                        </Box>
                        <Icon id="fill.arrow.thin-right" color="base.subtle" size="16" />
                    </Box>
                </Tooltip>
            </Card>
        </Link>
    );
};

TopSubcategoryCard.displayName = 'TopSubcategoryCard';

export default TopSubcategoryCard;
